import { BackButton } from 'components/Mobile/Mobile.styles'
import { ChevronLeft } from 'lucide-react'
import { useEffect } from 'react'
import { createPortal } from 'react-dom'

interface Props {
  header: React.ReactNode
  footer: React.ReactNode
  children: React.ReactNode
  handleBack?: () => void
  backLabel?: string
  backIcon?: React.ReactNode
}

export function MobileModal({
  header,
  footer,
  children,
  handleBack,
  backLabel = 'Back',
  backIcon,
}: Props) {
  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = ''
    }
  }, [])

  // Layout (FIR-7449): the modal occupies the band between the mobile top bar
  // (51px) and the bottom nav (60px) as a flex column with three regions:
  // a non-shrinking header, a single internally-scrolling body, and a pinned
  // footer. The previous implementation scrolled the whole container, let the
  // header scroll away, positioned the footer with `fixed bottom-[60px]` (so it
  // floated over the body and needed a magic `paddingBottom: 220` to compensate),
  // and ran 51px past the viewport bottom — which on mobile produced the
  // inconsistent / locked scrolling reported in the ticket. `overscroll-contain`
  // stops touch-scroll from chaining to the page behind the modal.
  return createPortal(
    <div className="fixed inset-x-0 top-[48px] bottom-[calc(60px+env(safe-area-inset-bottom))] bg-white flex flex-col z-50">
      {handleBack && (
        <BackButton onClick={handleBack}>
          {backIcon ?? <ChevronLeft size={20} />}
          <span>{backLabel}</span>
        </BackButton>
      )}
      <div className="flex-shrink-0 p-2 bg-gray-50 flex flex-col border">
        {header}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-2">
        {children}
      </div>
      <div className="flex-shrink-0 border-t bg-gray-50 p-2 w-full">
        {footer}
      </div>
    </div>,
    document.body
  )
}
